// Retry only idempotent reads. Saving and starting builds never enter this loop.
export async function readTemplateResponse(response) {
  if (response.ok) return response;
  let value;
  try { value = JSON.parse(await response.text()); } catch { value = null; }
  const detail = value?.detail;
  const error = new Error(typeof detail === 'string' ? detail : detail?.message ||
    'The deck could not be loaded. Please try again.');
  error.status = response.status;
  throw error;
}

export function startTemplateDeckPolling(load, onDeck, onError, schedule = setTimeout, cancel = clearTimeout) {
  let active = true; let timer; let failures = 0;
  const poll = async () => {
    try {
      const deck = await load();
      if (!active) return;
      failures = 0; onError(''); await onDeck(deck);
      if (active && !['ready', 'failed', 'cancelled'].includes(deck.status)) timer = schedule(poll, 2500);
    } catch (error) {
      if (!active) return;
      failures += 1;
      const transient = error.status == null || error.status === 408 || error.status === 429 || error.status >= 500;
      if (transient && failures <= 3) {
        onError('Connection interrupted. Reconnecting to the saved build…', { retrying: true });
        timer = schedule(poll, Math.min(2000 * failures, 6000));
      } else {
        onError(transient ? 'We couldn’t reconnect. Check your connection, then reconnect to this saved build.' : error.message || 'The deck could not be loaded.', { retrying: false });
      }
    }
  };
  poll();
  return () => { active = false; cancel(timer); };
}

// Bound every status/open request, including its body, and cancel it on unmount.
export async function requestTemplateResponse(url, options = {}, timeoutMs = 20000, fetcher = fetch) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  options.signal?.addEventListener('abort', abort, { once: true });
  if (options.signal?.aborted) controller.abort();
  const timer = setTimeout(abort, timeoutMs);
  try {
    const response = await fetcher(url, { ...options, signal: controller.signal });
    const body = await response.text();
    return { ok: response.ok, status: response.status, text: async () => body,
      json: async () => JSON.parse(body) };
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener('abort', abort);
  }
}
